import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from './components/error/ErrorBoundary';
import { createLazyRoute } from './utils/routeUtils';
import ProtectedRoute from './components/auth/ProtectedRoute';
import StandaloneProtectedRoute from './components/auth/StandaloneProtectedRoute';
import { ToastProvider } from './components/layout/ToastProvider';
import { createAppQueryClient } from '@/lib/react-query-client';

// Lazy load pages for better performance
const LoginPage = createLazyRoute(() => import('./pages/auth/LoginPage'));
const PinSwitchScreen = createLazyRoute(() => import('./pages/auth/PinSwitchScreen'));
const VerifySuccessPage = createLazyRoute(() => import('./pages/auth/VerifySuccessPage'));
const DashboardPage = createLazyRoute(() => import('./pages/dashboard/DashboardPage'));
const MembersPage = createLazyRoute(() => import('./pages/members/MembersPage'));
const MemberViewPage = createLazyRoute(() => import('./pages/members/MemberViewPage'));
const NewMemberPage = createLazyRoute(() => import('./pages/members/NewMemberPage'));
const EditMemberPage = createLazyRoute(() => import('./pages/members/EditMemberPage'));
const DeletedMembersPage = createLazyRoute(() => import('./pages/members/DeletedMembersPage'));
const SignWaiverPage = createLazyRoute(() => import('./pages/members/SignWaiverPage'));
const WaiverThankYouPage = createLazyRoute(() => import('./pages/members/WaiverThankYouPage'));
const ClassesPage = createLazyRoute(() => import('./pages/classes/ClassesPage'));
const ClassViewPage = createLazyRoute(() => import('./pages/classes/ClassViewPage'));
const NewClassPage = createLazyRoute(() => import('./pages/classes/NewClassPage'));
const EditClassPage = createLazyRoute(() => import('./pages/classes/EditClassPage'));
const CallBoothsPage = createLazyRoute(() => import('./pages/call-booths/CallBoothsPage'));
const LockersPage = createLazyRoute(() => import('./pages/lockers/LockersPage'));
const ClassCategoriesPage = createLazyRoute(() => import('./pages/class-categories/ClassCategoriesPage'));
const NewClassCategoryPage = createLazyRoute(() => import('./pages/class-categories/NewClassCategoryPage'));
const ClassCategoryViewPage = createLazyRoute(() => import('./pages/class-categories/ClassCategoryViewPage'));
const EditClassCategoryPage = createLazyRoute(() => import('./pages/class-categories/EditClassCategoryPage'));
const ClassLocationsPage = createLazyRoute(() => import('./pages/class-locations/ClassLocationsPage'));
const NewClassLocationPage = createLazyRoute(() => import('./pages/class-locations/NewClassLocationPage'));
const ClassLocationViewPage = createLazyRoute(() => import('./pages/class-locations/ClassLocationViewPage'));
const EditClassLocationPage = createLazyRoute(() => import('./pages/class-locations/EditClassLocationPage'));
const CoachesPage = createLazyRoute(() => import('./pages/coaches/CoachesPage'));
const NewCoachPage = createLazyRoute(() => import('./pages/coaches/NewCoachPage'));
const CoachViewPage = createLazyRoute(() => import('./pages/coaches/CoachViewPage'));
const EditCoachPage = createLazyRoute(() => import('./pages/coaches/EditCoachPage'));
const EmployeesPage = createLazyRoute(() => import('./pages/employees/EmployeesPage'));
const EmployeesOrgChartPage = createLazyRoute(() => import('./pages/employees/EmployeesOrgChartPage'));
const EmployeeViewPage = createLazyRoute(() => import('./pages/employees/EmployeeViewPage'));
const InventoryPage = createLazyRoute(() => import('./pages/inventory/InventoryPage'));
const MembershipsPage = createLazyRoute(() => import('./pages/memberships/MembershipsPage'));
const MembershipViewPage = createLazyRoute(() => import('./pages/memberships/MembershipViewPage'));
const EditMembershipPage = createLazyRoute(() => import('./pages/memberships/EditMembershipPage'));
const NewMembershipPage = createLazyRoute(() => import('./pages/memberships/NewMembershipPage'));
const MembershipTypesPage = createLazyRoute(() => import('./pages/membership-types/MembershipTypesPage'));
const NewMembershipTypePage = createLazyRoute(() => import('./pages/membership-types/NewMembershipTypePage'));
const MembershipTypeViewPage = createLazyRoute(() => import('./pages/membership-types/MembershipTypeViewPage'));
const EditMembershipTypePage = createLazyRoute(() => import('./pages/membership-types/EditMembershipTypePage'));
const AdminsPage = createLazyRoute(() => import('./pages/admins/AdminsPage'));
const AdminUserViewPage = createLazyRoute(() => import('./pages/admins/AdminUserViewPage'));
const EditAdminUserPage = createLazyRoute(() => import('./pages/admins/EditAdminUserPage'));
const NewAdminUserPage = createLazyRoute(() => import('./pages/admins/NewAdminUserPage'));
const CheckInsPage = createLazyRoute(() => import('./pages/check-ins/CheckInsPage'));
const BirthdaysPage = createLazyRoute(() => import('./pages/birthdays/BirthdaysPage'));
const AgentPage = createLazyRoute(() => import('./pages/agent/AgentPage'));
const ActivityLogsPage = createLazyRoute(() => import('./pages/activity-logs/ActivityLogsPage'));
const StyleBookPage = createLazyRoute(() => import('./pages/StyleBookPage'));
const ReportsPage = createLazyRoute(() => import('./pages/reports/ReportsPage'));
const OdooPage = createLazyRoute(() => import('./pages/odoo/OdooPage'));
const PersonalTrainingPage = createLazyRoute(() => import('./pages/personal-training/PersonalTrainingPage'));
const NewPTSessionPage = createLazyRoute(() => import('./pages/pt-sessions/NewPTSessionPage'));
const PTSessionViewPage = createLazyRoute(() => import('./pages/pt-sessions/PTSessionViewPage'));
const EditPTSessionPage = createLazyRoute(() => import('./pages/pt-sessions/EditPTSessionPage'));
const NewPTPackPage = createLazyRoute(() => import('./pages/pt-packs/NewPTPackPage'));
const PTPackViewPage = createLazyRoute(() => import('./pages/pt-packs/PTPackViewPage'));
const EditPTPackPage = createLazyRoute(() => import('./pages/pt-packs/EditPTPackPage'));
const PTPackTypesPage = createLazyRoute(() => import('./pages/pt-pack-types/PTPackTypesPage'));
const NewPTPackTypePage = createLazyRoute(() => import('./pages/pt-pack-types/NewPTPackTypePage'));
const PTPackTypeViewPage = createLazyRoute(() => import('./pages/pt-pack-types/PTPackTypeViewPage'));
const EditPTPackTypePage = createLazyRoute(() => import('./pages/pt-pack-types/EditPTPackTypePage'));
const GiftCardsPage = createLazyRoute(() => import('./pages/gift-cards/GiftCardsPage'));
const NewGiftCardPage = createLazyRoute(() => import('./pages/gift-cards/NewGiftCardPage'));
const GiftCardViewPage = createLazyRoute(() => import('./pages/gift-cards/GiftCardViewPage'));
const BpaPage = createLazyRoute(() => import('./pages/bpa/BpaPage'));
const BpaViewPage = createLazyRoute(() => import('./pages/bpa/BpaViewPage'));
const NewBpaPage = createLazyRoute(() => import('./pages/bpa/NewBpaPage'));
const EditBpaPage = createLazyRoute(() => import('./pages/bpa/EditBpaPage'));
const PublicSchedulePage = createLazyRoute(() => import('./pages/public/PublicSchedulePage'));
const PublicClassProfilePage = createLazyRoute(() => import('./pages/public/PublicClassProfilePage'));
const PublicLeaderboardPage = createLazyRoute(() => import('./pages/public/PublicLeaderboardPage'));
const BoothDisplayPage = createLazyRoute(() => import('./pages/public/BoothDisplayPage'));

// Create QueryClient instance using centralized configuration
const queryClient = createAppQueryClient();

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-success" element={<VerifySuccessPage />} />
              <Route path="/schedule" element={<PublicSchedulePage />} />
              <Route path="/schedule/:id" element={<PublicClassProfilePage />} />
              <Route path="/leaderboard" element={<PublicLeaderboardPage />} />
              <Route path="/booth-display" element={<BoothDisplayPage />} />
              
              {/* Standalone pages without sidebar */}
              <Route element={<StandaloneProtectedRoute />}>
                <Route path="/switch" element={<PinSwitchScreen />} />
                <Route path="/members/:memberId/sign-waiver" element={<SignWaiverPage />} />
                <Route path="/members/:memberId/waiver-thank-you" element={<WaiverThankYouPage />} />
              </Route>
              
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/members" element={<MembersPage />} />
                <Route path="/members/new" element={<NewMemberPage />} />
                <Route path="/members/:id" element={<MemberViewPage />} />
                <Route path="/members/:id/edit" element={<EditMemberPage />} />
                <Route path="/members/deleted" element={<DeletedMembersPage />} />
                <Route path="/classes" element={<ClassesPage />} />
                <Route path="/classes/new" element={<NewClassPage />} />
                <Route path="/classes/:id/view" element={<ClassViewPage />} />
                <Route path="/classes/:id/edit" element={<EditClassPage />} />
                <Route path="/spaces" element={<CallBoothsPage />} />
                {/* Renamed when the Library meeting room joined the booths. Kept so
                    existing bookmarks and the old nav link still land. */}
                <Route path="/call-booths" element={<Navigate to="/spaces" replace />} />
                <Route path="/lockers" element={<LockersPage />} />
                <Route path="/class-categories" element={<ClassCategoriesPage />} />
                <Route path="/class-categories/new" element={<NewClassCategoryPage />} />
                <Route path="/class-categories/:id" element={<ClassCategoryViewPage />} />
                <Route path="/class-categories/:id/edit" element={<EditClassCategoryPage />} />
                <Route path="/class-locations" element={<ClassLocationsPage />} />
                <Route path="/class-locations/new" element={<NewClassLocationPage />} />
                <Route path="/class-locations/:id" element={<ClassLocationViewPage />} />
                <Route path="/class-locations/:id/edit" element={<EditClassLocationPage />} />
                <Route path="/coaches" element={<CoachesPage />} />
                <Route path="/coaches/new" element={<NewCoachPage />} />
                <Route path="/coaches/:id" element={<CoachViewPage />} />
                <Route path="/coaches/:id/edit" element={<EditCoachPage />} />
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/employees/org-chart" element={<EmployeesOrgChartPage />} />
                <Route path="/employees/:id" element={<EmployeeViewPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/personal-training" element={<PersonalTrainingPage />} />
                <Route path="/pt-sessions" element={<Navigate to="/personal-training?tab=sessions" replace />} />
                <Route path="/pt-sessions/new" element={<NewPTSessionPage />} />
                <Route path="/pt-sessions/:id" element={<PTSessionViewPage />} />
                <Route path="/pt-sessions/:id/edit" element={<EditPTSessionPage />} />
                <Route path="/pt-packs" element={<Navigate to="/personal-training" replace />} />
                <Route path="/pt-packs/new" element={<NewPTPackPage />} />
                <Route path="/pt-packs/:id" element={<PTPackViewPage />} />
                <Route path="/pt-packs/:id/edit" element={<EditPTPackPage />} />
                <Route path="/pt-pack-types" element={<PTPackTypesPage />} />
                <Route path="/pt-pack-types/new" element={<NewPTPackTypePage />} />
                <Route path="/pt-pack-types/:id" element={<PTPackTypeViewPage />} />
                <Route path="/pt-pack-types/:id/edit" element={<EditPTPackTypePage />} />
                <Route path="/memberships" element={<MembershipsPage />} />
                <Route path="/memberships/new" element={<NewMembershipPage />} />
                <Route path="/memberships/:id" element={<MembershipViewPage />} />
                <Route path="/memberships/:id/edit" element={<EditMembershipPage />} />
                <Route path="/gift-cards" element={<GiftCardsPage />} />
                <Route path="/gift-cards/new" element={<NewGiftCardPage />} />
                <Route path="/gift-cards/:id" element={<GiftCardViewPage />} />
                <Route path="/membership-types" element={<MembershipTypesPage />} />
                <Route path="/membership-types/new" element={<NewMembershipTypePage />} />
                <Route path="/membership-types/:id" element={<MembershipTypeViewPage />} />
                <Route path="/membership-types/:id/edit" element={<EditMembershipTypePage />} />
                <Route path="/admin-users" element={<AdminsPage />} />
                <Route path="/admin-users/:id" element={<AdminUserViewPage />} />
                <Route path="/admin-users/:id/edit" element={<EditAdminUserPage />} />
                <Route path="/admin-users/new" element={<NewAdminUserPage />} />
                <Route path="/check-ins" element={<CheckInsPage />} />
                <Route path="/birthdays" element={<BirthdaysPage />} />
                <Route path="/agent" element={<AgentPage />} />
                <Route path="/activity-logs" element={<ActivityLogsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/style-book" element={<StyleBookPage />} />
                <Route path="/odoo" element={<OdooPage />} />
                <Route path="/bpa" element={<BpaPage />} />
                <Route path="/bpa/new" element={<NewBpaPage />} />
                <Route path="/bpa/:id" element={<BpaViewPage />} />
                <Route path="/bpa/:id/edit" element={<EditBpaPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
        {/* React Query DevTools - Available in all environments for debugging */}
        <ReactQueryDevtools 
          initialIsOpen={false}
          position="bottom"
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;